import React, { useEffect, useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import api from "@/lib/api";
import { Download, ChevronDown, FileText, Clock, AlertCircle, ShieldCheck, Calendar, Briefcase, Heart } from "lucide-react";

export default function Rights() {
  const { t, lang } = useLang();
  const [resources, setResources] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get("/resources?category=Collective Agreement").then((r) => setResources(r.data)).catch(() => {});
  }, []);
  const steps = t("rights.grievanceSteps");
  const cards = t("rights.workplaceCards");
  const faqs = t("rights.faqs");
  const cardIcons = [Clock, Briefcase, ShieldCheck, Calendar, AlertCircle, Heart];

  return (
    <div data-testid="rights-page">
      <section className="bg-gradient-to-br from-pink-50 via-white to-rose-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900">{t("rights.title")}</h1>
          <div className="h-1 w-20 cupe-gradient rounded-full mt-4" />
        </div>
      </section>

      {/* Collective Agreement */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-display text-3xl font-bold text-slate-900 mb-2">{t("rights.agreementTitle")}</h2>
        <p className="text-slate-600 mb-8">{t("rights.agreementSub")}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {resources.map((r) => (
            <a key={r.id} href={r.file_url} target="_blank" rel="noreferrer" download className="flex items-start gap-4 bg-white border border-slate-200 rounded-xl p-5 hover:border-[#d81b7a] hover:shadow-md transition" data-testid="ca-download">
              <div className="w-12 h-12 rounded-lg cupe-gradient flex items-center justify-center flex-shrink-0">
                <FileText className="text-white" size={22} />
              </div>
              <div className="flex-1">
                <div className="font-display font-semibold text-slate-900">{lang === "fr" ? r.title_fr : r.title_en}</div>
                <div className="text-sm text-slate-500 mt-1">{lang === "fr" ? r.description_fr : r.description_en}</div>
              </div>
              <Download className="text-[#d81b7a] flex-shrink-0" />
            </a>
          ))}
        </div>
      </section>

      {/* Grievance Process Timeline */}
      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-slate-900 mb-2">{t("rights.grievanceTitle")}</h2>
          <div className="h-1 w-16 cupe-gradient rounded-full mb-10" />
          <div className="relative">
            <div className="absolute left-6 top-0 bottom-0 w-1 cupe-gradient rounded-full hidden md:block" />
            <ol className="space-y-6">
              {Array.isArray(steps) && steps.map((s, i) => (
                <li key={i} className="flex gap-5 md:gap-8 items-start" data-testid={`grievance-step-${i}`}>
                  <div className="w-12 h-12 cupe-gradient text-white rounded-full flex items-center justify-center font-display font-bold text-lg flex-shrink-0 z-10 shadow-lg shadow-pink-200">{i + 1}</div>
                  <div className="bg-white border border-slate-200 rounded-xl p-5 flex-1">
                    <div className="font-display font-semibold text-slate-900 text-lg">{s.t}</div>
                    <p className="text-slate-600 mt-1">{s.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Workplace Rights */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-display text-3xl font-bold text-slate-900 mb-2">{t("rights.workplaceTitle")}</h2>
        <div className="h-1 w-16 cupe-gradient rounded-full mb-10" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.isArray(cards) && cards.map((c, i) => {
            const Icon = cardIcons[i % cardIcons.length];
            return (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 hover:-translate-y-1 hover:shadow-md transition-all" data-testid={`workplace-${i}`}>
                <div className="w-12 h-12 rounded-lg bg-pink-50 text-[#d81b7a] flex items-center justify-center mb-4"><Icon size={22} /></div>
                <div className="font-display font-semibold text-slate-900 text-lg">{c.t}</div>
                <p className="text-slate-600 mt-2 leading-relaxed">{c.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQs */}
      <section className="bg-slate-50 border-t border-slate-200 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-slate-900 mb-2">{t("rights.faqTitle")}</h2>
          <div className="h-1 w-16 cupe-gradient rounded-full mb-10" />
          <div className="space-y-3">
            {Array.isArray(faqs) && faqs.map((f, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`faq-${i}`}>
                <button
                  onClick={() => setOpen(open === i ? null : i)}
                  className="w-full text-left px-5 py-4 flex justify-between items-center gap-4 hover:bg-pink-50/40"
                >
                  <span className="font-display font-semibold text-slate-900">{f.q}</span>
                  <ChevronDown className={`transition-transform ${open === i ? "rotate-180" : ""}`} size={20} />
                </button>
                {open === i && <div className="px-5 pb-5 text-slate-600 leading-relaxed">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
